import { RootState } from '@/store/store';

/**
 * Get all pricing metrics across all pages
 * @param state Redux state
 * @returns Array of pricing metrics
 */
export const selectAllPricingMetrics = (state: RootState): any[] => {
  if (!state.company.company || !state.company.company.pages) {
    return [];
  }

  const allMetrics: any[] = [];

  // Collect metrics from all pages
  for (const page of state.company.company.pages) {
    // Check both pricing_metrics and pricingMetrics arrays (API might use either naming)
    const metrics = page.pricing_metrics || page.pricingMetrics || [];
    allMetrics.push(...metrics);
  }

  // Remove duplicates by ID
  const uniqueMetrics = allMetrics.reduce((acc: any[], metric: any) => {
    if (!acc.some((m: any) => m.id === metric.id)) {
      acc.push(metric);
    }
    return acc;
  }, []);

  return uniqueMetrics;
};

/**
 * Finds a pricing metric by its ID across all pages of a company
 * @param state Redux state
 * @param metricId The ID of the pricing metric to look up
 * @returns The pricing metric object if found, or null
 */
export const selectPricingMetricById = (state: RootState, metricId: string | null | undefined): any | null => {
  if (!metricId || !state.company.company || !state.company.company.pages) {
    return null;
  }

  // Look through all pages
  for (const page of state.company.company.pages) {
    // Check both pricing_metrics and pricingMetrics arrays (API might use either naming)
    const metrics = page.pricing_metrics || page.pricingMetrics || [];
    
    // Find the metric with the matching ID
    const metric = metrics.find((m: any) => m.id === metricId);
    if (metric) {
      return metric;
    }
  }

  return null;
};

/**
 * Get the display name of a pricing metric
 * @param state Redux state
 * @param metricId The ID of the pricing metric
 * @returns The display name of the metric, or empty string if not found
 */
export const selectPricingMetricName = (state: RootState, metricId: string | null | undefined): string => {
  const metric = selectPricingMetricById(state, metricId);
  return metric ? metric.display_name || '' : '';
};

/**
 * Get the modality of a pricing metric
 * @param state Redux state
 * @param metricId The ID of the pricing metric
 * @returns The modality of the metric, or null if not found
 */
export const selectPricingMetricModality = (state: RootState, metricId: string | null | undefined): string | null => {
  const metric = selectPricingMetricById(state, metricId);
  return metric ? metric.modality || null : null;
};
